/*
    Copyright (C) Paul B. Lucas 2025, 2026  ALL RIGHTS RESERVED
    Licensed to the University of Illinois Urbana Champaign

    This is a Javascript composable that uses the assets API endpoint to fetch and store assets
    from/to the database.   Three methods are incuded:

        fetchAssetsOnOrgs -     selects assets based on a list of provided org codes
        fetchAssetsOnQuery -    selects assets based on a generalized query array of 
                                key:value pairs.
        saveAssete -            saves a single asset to database
    

    The fetchs are async functions that awaits a results of an axios call to the RWA API. When promise 
    is resolved, the results are returned in agreements ref.

    API configuration data is stored as JSON file in src/configuration/ and imported as a JSON array.  Import
    can do this from a basic JSON file.  Parameters in the config array include the base-url, the POSTQL ccf
    endpoint, and authentication data for the axios headers (API key).  Details:

        {
            "apim-key-name":    "Bearer",
            "apim-key":         "--- some key ---",
            "base-url":         "https://api.elam.fs.illinois.edu",
            "ccfURL":           "https://api.elam.fs.illinois.edu/ccf",
            "agreement-path":   "/agreements",
            "employee-path":    "/employees",
            "table-path":       "/tables",
            "assets-path":      "/assets",
            "nans":             "/nans",
            "image-url":        "/https://images.elam.fs.illinois.edu/employees/"     
        }     
        
    If the API URL changes in DNS, this file must be modified.

    <Revision History>
        02/25/2025      pblucas     created 
        03/16/2026      pblucas     Corrected typos & updated copyright
      
      
    </Revision History>
*/

import { ref } from 'vue'
import axios from 'axios'
import amApiConfigs from '@/configuration/amAPI.config.json'

const useAssetsAPI = () =>    {

    // DO NOT USE DEFAULT FOR AUTH IF AXIOS IS ACCESSING MORE THAN ONE API
 
    const baseURL  = amApiConfigs['base-url']

    // set specific endpoint for employees
    const URIQS         =  "?v=2"
    const URIOrgs       =  'owner-org/'
    const URIAssetsPath =   amApiConfigs['assets-path']+'/'
    
    //use the POSTQL endpoint 'ccf' for storing
    const ccfURL        =   amApiConfigs['ccfURL']
    
    // result reference to return assets: array
    const assetResults  =   ref([])           
    // reference for returned data on where data was stored (includes pkey, assigned uuid, &cta)
    const assetKeyData  =   ref({})

    const  fetchAssetsOnQuery = async (aQuery)  =>  {

        // convert array of key:value pairs to / delimited components for  URL
        // e.g.,  ["first-name: "john", "last-name":"Doe"]  =>  /first-name/John/last-name/Doe
        let uri = Object.keys(aQuery).map((k) => { 
        return encodeURIComponent(k) + '/' + encodeURIComponent(aQuery[k]);
        }).join('/')
               
        let url =  baseURL + URIAssetsPath + uri + URIQS     
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {   
            assetResults.value      =   response.data.results     
        })
        .catch( error =>  {
            console.log("FetchAssets:: "+ error.message)
            assetResults.value = []
        })
       
    }   // end function

    const  deleteAssetsOnQuery = async (aQuery)  =>  {

        // convert array of key:value pairs to / delimited components for  URL
        // e.g.,  ["first-name: "john", "last-name":"Doe"]  =>  /first-name/John/last-name/Doe
        let uri = Object.keys(aQuery).map((k) => { 
        return encodeURIComponent(k) + '/' + encodeURIComponent(aQuery[k]);
        }).join('/')
               
        let url =  baseURL + URIAssetsPath + uri    
        await axios({
            method:         'delete',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {   
            assetResults.value      =   response.data.response     
        })
        .catch( error =>  {
            console.log("DeleteAssets:: "+ error.message)
        })
       
    }   // end function

    const  fetchAssetsOnOrgs = async (aOrgs)  =>  {
    
        var assets = []
        
        let resolvedPromises = await Promise.allSettled(aOrgs.map(org => {
            let url =  baseURL + URIAssetsPath + URIOrgs + org + URIQS    
            let data =  axios({
                method:         'get',
                url:            url,
                headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            return data;
        }))
        // have an array of settled promised with either resolve or response callbacks
        resolvedPromises.forEach((rp, num)  =>  {

            if(rp.status == 'rejected') {
                console.log("Axios promise rejected: fetching orgcode: "+aOrgs[num])
                //throw Error ("Axios promise rejected: fetching orgcode: "+aOrgs[num])
            }
            else if(rp.status == 'fulfilled')  {
                // data available on promise
                const data = rp.value.data      
 
                // do some more detailed error checking on API return
                // data is JSON
                if(!(data.response['http_return'] == 202) || (data.response['http_return'] == 200)) { 
 
                    console.log("FetchAssets:: error thrown in response")
                    //throw Error ("Unable to fetch asset data")
                }
                if(data.results.length == 0) { 
                    console.log("FetchAssets:: No assets found for specified org")
                    //throw Error ("No assets found on orgcode "+ aOrgs[num])
                }
                // completed and valid query returns
                //console.log("FetchAssets:: on orgs: "+aOrgs+" with results: "+JSON.stringify(data.results))
                for (let k in data.results)  {
                
                    // get agreement object from API call returned results[]
                    let obj = data.results[k]    
                    assets.push(obj)
                    // console.log("Pushing agreement: " + JSON.stringify(obj))    
                }
                assetResults.value = assets
            }  
        })  
          
    }

    // argument 'asset' is a ref object (single) or array
    // JSON stringify resolves ambiguity
    const storeAsset =  async (asset) => {

        // need to generate attributes data - JSON array
        const payload = JSON.stringify(asset)

        let formData = new FormData()
        formData.append("collection","assets")

        // we will update the asset on a null query string using an POST method
        // and rely on the UPDATE ON DUPLICATE KEY (UUID) in the storeAssets() database method
        // 
        formData.append("action","POST")
        formData.append("query","{}")
        formData.append("attributes",payload)
        formData.append("parameters", "{}")
        try {
            await axios({
                method: 'POST',
                url: ccfURL,
                data: formData,
                headers: {'Content-Type': 'multipart/form-data',
                          'Authorization': `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            .then( (response) => {
                if (!response.status == 200)   {
                    throw Error ("Axios call failed")
                }
                assetKeyData.value = response.data.results                     
            })
        }
        catch(error)   {
            console.log("StoreAsset:: error: "+error.message)
        }        
    }
    return {assetResults, assetKeyData, fetchAssetsOnQuery, deleteAssetsOnQuery, fetchAssetsOnOrgs, storeAsset}
}

export default useAssetsAPI